import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CLUB_GENRES, MAX_CLUB_GENRES, type ActivityItem, type Club, type ClubBook, type Member, type UserSummary } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { Avatar, RolePill } from "./ui";
import { BlockedList, JoinRequests } from "./PublicClub";
import { ProfileFace, useOpenProfile } from "./Profile";
import { CalendarIcon, CheckIcon, ClubIcon, LampIcon, MoreIcon, PencilIcon, PinIcon, PlusIcon, ShelfIcon, VoteIcon } from "./Icons";

/*
 * The Club tab's parts (PLAN.md §Club tab): the club in numbers, recent
 * activity, member rows that open to a little more, and the club's genres.
 * Built from what the app already has: cards, the deck row's ⋯ menu, pills.
 */

/** "just now", "5h", "3d", "2w", then a date. */
export function agoShort(iso: string) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.round(hrs / 24);
  if (days < 14) return `${days}d`;
  if (days < 60) return `${Math.round(days / 7)}w`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const sinceLine = (iso: string) => {
  const d = new Date(iso);
  const months = (new Date().getFullYear() - d.getFullYear()) * 12 + new Date().getMonth() - d.getMonth();
  const years = Math.floor(months / 12);
  const when = d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  if (months > 0 && months % 12 === 0) return `since ${when} · ${years === 1 ? "one year" : `${years} years`} this month`;
  return `since ${when}`;
};

/** A few figures about the club as a whole, and the book it liked best. */
export function ClubNumbers({ clubId }: { clubId: string }) {
  const q = useQuery({ queryKey: ["club-stats", clubId], queryFn: () => api.clubStats(clubId) });
  if (!q.data) return null;
  const s = q.data;
  // Numbers only mean something once the club has read something together.
  if (s.booksRead === 0) return <p className="muted small story-line">Your club's story starts with the first book.</p>;
  const figures: [string, string][] = [
    [s.members.toLocaleString(), s.members === 1 ? "member" : "members"],
    [s.booksRead.toLocaleString(), s.booksRead === 1 ? "book read" : "books read"],
    [s.pages.toLocaleString(), "pages"],
    [s.meetings.toLocaleString(), s.meetings === 1 ? "meeting" : "meetings"],
    [s.comments.toLocaleString(), s.comments === 1 ? "comment" : "comments"],
    [s.averageRating != null ? s.averageRating.toFixed(1) : "—", "average rating"],
  ];
  return (
    <section className="stack-sm">
      <div className="sect"><h3>The club in numbers</h3><span className="count">{sinceLine(s.foundedAt)}</span></div>
      <div className="card nums">
        {figures.map(([n, label]) => <div key={label} className="n"><b>{n}</b><span>{label}</span></div>)}
        {s.best && (
          <div className="best">
            {s.best.coverUrl ? <img src={s.best.coverUrl} alt="" className="mini-cover" /> : <span className="mini-cover" style={{ background: s.best.coverTone || "var(--surface-2)" }} />}
            <div>
              <div className="muted small">Best loved</div>
              <div><b className="serif">{s.best.title}</b> <span className="muted small">★ {s.best.rating.toFixed(1)}{s.best.finishedAt ? ` · read ${new Date(s.best.finishedAt).toLocaleDateString(undefined, { month: "short", year: "numeric" })}` : ""}</span></div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/** "Ivo", "Ivo and Jade", "Ivo, Jade and 3 others". */
function names(people: UserSummary[], others = 0, meId?: string) {
  const ns = people.map((p) => (p.id === meId ? "You" : p.name));
  if (others > 0) return `${ns.join(", ")} and ${others} ${others === 1 ? "other" : "others"}`;
  if (ns.length <= 1) return ns[0] ?? "Someone";
  return `${ns.slice(0, -1).join(", ")} and ${ns[ns.length - 1]}`;
}

/** One activity line: an icon, what happened, and a muted line of detail with how long ago. */
function line(it: ActivityItem, meId?: string): { icon: React.ReactNode; main: React.ReactNode; sub: string } {
  const who = names(it.actors, it.others, meId);
  const t = it.book ? <b>{it.book.title}</b> : null;
  const ago = agoShort(it.at);
  switch (it.kind) {
    case "board_posted": return { icon: <PinIcon />, main: <>{it.count && it.count > 1 ? <>{it.count} new posts on the noticeboard</> : <>{who} posted on the noticeboard</>}</>, sub: [it.count && it.count > 1 ? names(it.actors, it.others, meId) : null, ago].filter(Boolean).join(" · ") };
    case "created": return { icon: <ClubIcon />, main: <>{who} started the club</>, sub: ago };
    case "joined": return { icon: <ClubIcon />, main: <>{who} joined</>, sub: ago };
    case "visibility": return { icon: <ClubIcon />, main: <>{who} {{ open: "made the club open: anyone can join", request: "listed the club: people can ask to join", private: "made the club private" }[it.visibility ?? "private"]}</>, sub: ago };
    case "started": return { icon: <LampIcon />, main: <>{who} started {t}</>, sub: [it.picked === "vote" ? "The club voted for it" : it.picked === "turn" ? `${who}'s turn to pick` : `Picked by ${it.actors[0]?.id === meId ? "you" : who}`, ago].join(" · ") };
    case "finished": return { icon: <ShelfIcon />, main: <>{t} finished</>, sub: [it.rating != null ? `rated ${it.rating.toFixed(1)} on average` : null, ago].filter(Boolean).join(" · ") };
    case "suggested": return { icon: <PlusIcon />, main: <>{who} suggested {t}</>, sub: ago };
    case "vote_opened": return { icon: <VoteIcon />, main: <>A vote opened on the next book</>, sub: `${who} opened it · ${ago}` };
    case "vote_closed": return { icon: <VoteIcon />, main: it.winner ? <><b>{it.winner.title}</b> won the vote</> : <>The vote closed</>, sub: ago };
    case "meeting_set": return {
      icon: <CalendarIcon />,
      main: <>Meeting set for <b>{it.meetingAt ? new Date(it.meetingAt).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "a new date"}</b></>,
      sub: [it.location, ago].filter(Boolean).join(" · "),
    };
    case "reader_finished": return { icon: <CheckIcon />, main: <>{who} finished {t}</>, sub: ago };
    case "role_changed": return { icon: <ClubIcon />, main: <>{it.target ? (it.target.id === meId ? "You are" : `${it.target.name} is`) : "Someone is"} {it.role === "admin" ? "now an admin" : "no longer an admin"}</>, sub: ago };
  }
}

/** The club's recent activity, eight lines at a time. */
export function ActivityFeed({ clubId, meId }: { clubId: string; meId?: string }) {
  const q = useInfiniteQuery({
    queryKey: ["club-activity", clubId],
    queryFn: ({ pageParam }) => api.clubActivity(clubId, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  if (!q.data || items.length === 0) return null;
  return (
    <section className="stack-sm">
      <div className="sect"><h3>Recent activity</h3></div>
      <div className="card feed">
        {items.map((it) => {
          const l = line(it, meId);
          return <div key={it.id} className="ev"><span className="ic" aria-hidden="true">{l.icon}</span><div style={{ minWidth: 0 }}><div className="ev-main">{l.main}</div><div className="muted small">{l.sub}</div></div></div>;
        })}
      </div>
      {q.hasNextPage && <button type="button" className="linkish small" style={{ alignSelf: "flex-start" }} disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>{q.isFetchingNextPage ? "Loading…" : "See more"}</button>}
    </section>
  );
}

/** One member: a compact line that opens to when they joined, books with the club, their place in the current book, and when they were last around. Admins get a ⋯. */
export function MemberRow({ m, club, meId, canManage, isOwner, current }: { m: Member; club: Club; meId?: string; canManage: boolean; isOwner: boolean; current?: ClubBook | null }) {
  const qc = useQueryClient();
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ["members"] }), qc.invalidateQueries({ queryKey: ["club"] }), qc.invalidateQueries({ queryKey: ["blocks"] })]);
  const setRole = useMutation({ mutationFn: (role: "admin" | "member") => api.setRole(club.id, m.id, role), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (block: boolean) => api.removeMember(club.id, m.id, block), onSuccess: refresh });
  const [menu, setMenu] = useState(false);
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState<false | "remove" | "block">(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setMenu(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);
  const self = m.id === meId;
  const openProfile = useOpenProfile();
  const manageable = canManage && !self && m.role !== "owner" && (isOwner || m.role === "member");
  const joined = new Date(m.joinedAt);
  const short = joined.toLocaleDateString(undefined, joined.getFullYear() === new Date().getFullYear() ? { month: "short", day: "numeric" } : { month: "short", year: "numeric" });
  const pages = current?.book.pages ?? null;
  const where = m.reading ? (m.reading.label || (pages ? `p. ${Math.round(pages * m.reading.percent / 100)} · ${m.reading.percent}%` : `${m.reading.percent}%`)) : null;

  return (
    <div className={`mrow-wrap${open ? " open" : ""}`} ref={ref}>
      <div className="mrow">
        <ProfileFace user={m} />
        <button type="button" className="mrow-hit" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          <span className="mn">{m.name}{self ? " (you)" : ""}</span>
          <RolePill role={m.role} />
          <span className="mj" title={`Joined ${joined.toLocaleDateString()}`}>{short}</span>
        </button>
        {manageable ? <button type="button" className="more" aria-label={`Manage ${m.name}`} aria-expanded={menu} onClick={() => setMenu((o) => !o)}><MoreIcon width={18} height={18} /></button> : <span className="more-gap" />}
        {menu && (
          <div className="menu" role="menu">
            {m.role === "member"
              ? <button role="menuitem" disabled={setRole.isPending} onClick={() => { setMenu(false); setRole.mutate("admin"); }}>Make admin</button>
              : <button role="menuitem" disabled={setRole.isPending} onClick={() => { setMenu(false); setRole.mutate("member"); }}>Remove admin</button>}
            <button role="menuitem" className="danger" onClick={() => { setMenu(false); setRemoving("remove"); }}>Remove from club</button>
            <button role="menuitem" className="danger" onClick={() => { setMenu(false); setRemoving("block"); }}>Remove and block</button>
          </div>
        )}
      </div>
      {open && (
        <div className="mcard">
          <div><span>Joined</span><b>{joined.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</b></div>
          <div><span>Books with the club</span><b>{m.booksFinished ?? 0} finished</b></div>
          {current && <div><span>{current.book.title}</span><b>{where ?? "not started"}</b></div>}
          <div><span>Last active</span><b>{m.lastActiveAt ? agoLong(m.lastActiveAt) : "not yet"}</b></div>
          <button type="button" className="linkish small mcard-card" onClick={() => openProfile(m.id)}>{self ? "Your library card" : `${m.name}'s library card`}</button>
        </div>
      )}
      {removing && (
        <div className="row small mrow-ask">
          <span className="muted">{removing === "block"
            ? `Remove ${m.name} and block them? They won't be able to join again, ask to, or use an invite link.`
            : `Remove ${m.name} from the club?${club.visibility === "open" ? " The club is open, so they could join again." : ""}`}</span>
          <button className="btn danger sm" disabled={remove.isPending} onClick={() => remove.mutate(removing === "block")}>{removing === "block" ? "Remove and block" : "Remove"}</button>
          <button className="btn ghost sm" onClick={() => setRemoving(false)}>Keep</button>
        </div>
      )}
      {(setRole.isError || remove.isError) && <p className="error small mrow-ask">{describeError(setRole.error ?? remove.error)}</p>}
    </div>
  );
}

/** "today", "yesterday", "3 days ago", "2 weeks ago", then a date. */
function agoLong(iso: string) {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days < 1) return "today";
  if (days < 2) return "yesterday";
  if (days < 14) return `${days} days ago`;
  if (days < 60) return `${Math.round(days / 7)} weeks ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** A member was last around this long ago, in days; never = Infinity. */
export const quietDays = (m: Member) => (m.lastActiveAt ? (Date.now() - new Date(m.lastActiveAt).getTime()) / 86_400_000 : Infinity);

/** The club's genres as pills. */
export function GenrePills({ genres }: { genres: string[] }) {
  if (genres.length === 0) return null;
  return <div className="genre-pills">{genres.map((g) => <span key={g} className="pill">{g}</span>)}</div>;
}

/** The genres the club's finished books are tagged with, most common first, mapped onto the fixed list. */
export function suggestedGenres(finished: ClubBook[]): string[] {
  const seen = new Map<string, number>();
  for (const b of finished) for (const tag of b.book.genres ?? []) {
    const hit = CLUB_GENRES.find((g) => g.toLowerCase() === tag.toLowerCase() || (g === "Science fiction" && /sci-?fi/i.test(tag)));
    if (hit) seen.set(hit, (seen.get(hit) ?? 0) + 1);
  }
  return [...seen.entries()].sort((a, b) => b[1] - a[1]).map(([g]) => g);
}

/** Tappable genre chips, up to five; the chosen and suggested ones first, ten shown until "More genres". Used by Settings and the creation flow. */
export function GenreChips({ value, onChange, suggested = [] }: { value: string[]; onChange: (v: string[]) => void; suggested?: string[] }) {
  const [all, setAll] = useState(false);
  const [first] = useState(() => [...new Set([...value, ...suggested, ...CLUB_GENRES])]); // the order holds still while you tap
  const shown = all ? first : first.slice(0, Math.max(10, value.length));
  const full = value.length >= MAX_CLUB_GENRES;
  const toggle = (g: string) => onChange(value.includes(g) ? value.filter((x) => x !== g) : [...value, g]);
  return (
    <div className="genre-pick" role="group" aria-label="Genres">
      {shown.map((g) => {
        const on = value.includes(g);
        return <button key={g} type="button" className={`gchip${on ? " on" : ""}`} aria-pressed={on} disabled={!on && full} onClick={() => toggle(g)}>{on && <CheckIcon aria-hidden="true" />}{g}</button>;
      })}
      {!all && first.length > shown.length && <button type="button" className="linkish small" onClick={() => setAll(true)}>More genres</button>}
    </div>
  );
}

/**
 * Up to five genres from the fixed list. At rest, the chosen ones and a
 * pencil; the pencil opens the chips with Save and Cancel, like every
 * other setting you type or pick.
 */
export function GenrePicker({ value, onSave, finished, saved }: { value: string[]; onSave: (v: string[]) => void; finished: ClubBook[]; saved?: React.ReactNode }) {
  const [editing, setEditing] = useState(false);
  const [pick, setPick] = useState<string[]>(value);
  useEffect(() => { if (!editing) setPick(value); }, [value, editing]);
  const suggested = suggestedGenres(finished);
  if (!editing) {
    return (
      <div className="field efield">
        <label htmlFor="s-genres-edit">What the club tends to read{saved}</label>
        <div className="eview">
          {value.length > 0 ? <div className="genre-pills ev-text">{value.map((g) => <span key={g} className="pill">{g}</span>)}</div>
            : <span className="ev-text empty">No genres yet</span>}
          <button id="s-genres-edit" type="button" className="pencil" aria-label="Edit genres" onClick={() => setEditing(true)}><PencilIcon width={16} height={16} /></button>
        </div>
      </div>
    );
  }
  const changed = pick.join("|") !== value.join("|");
  return (
    <div className="field">
      <label>What the club tends to read</label>
      <GenreChips value={pick} onChange={setPick} suggested={suggested} />
      <span className="muted small">Pick up to {MAX_CLUB_GENRES}{pick.length > 0 ? ` (${pick.length} chosen)` : ""}. They show on the Club tab and the invite page.{suggested.length > 0 && value.length === 0 ? " The first few match books your club has finished." : ""}</span>
      <div className="row" style={{ gap: 8 }}>
        <button type="button" className="btn primary sm" disabled={!changed} onClick={() => { onSave(pick); setEditing(false); }}>Save</button>
        <button type="button" className="btn ghost sm" onClick={() => setEditing(false)}>Cancel</button>
      </div>
    </div>
  );
}

/** The Club tab's members column: owner and admins, then up to twelve members and a way to all of them. */
export function MembersPreview({ members, club, meId, canManage, isOwner, current }: { members: Member[]; club: Club; meId?: string; canManage: boolean; isOwner: boolean; current: ClubBook | null }) {
  const rank = (m: Member) => (m.role === "owner" ? 0 : m.role === "admin" ? 1 : 2);
  const leads = members.filter((m) => m.role !== "member").sort((a, b) => rank(a) - rank(b));
  const rest = members.filter((m) => m.role === "member");
  // Newest first: the people most likely to need a hello, or a check.
  const shown = [...rest].sort((a, b) => b.joinedAt.localeCompare(a.joinedAt)).slice(0, PREVIEW);
  const row = (m: Member) => <MemberRow key={m.id} m={m} club={club} meId={meId} canManage={canManage} isOwner={isOwner} current={current} />;
  return (
    <section className="stack-sm members-col">
      <div className="row spread"><h2>Members</h2><span className="muted small num">{members.length}</span></div>
      {canManage && club.visibility === "request" && <JoinRequests clubId={club.id} />}
      {leads.length > 0 && <><div className="mgrp">{leads.length === 1 ? "Owner" : "Owner & admins"}</div><div className="mlist">{leads.map(row)}</div></>}
      {rest.length > 0 && <><div className="mgrp">{rest.length > PREVIEW ? "Newest members" : "Members"}</div><div className="mlist">{shown.map(row)}</div></>}
      {rest.length > PREVIEW && (
        <Link to="/clubs/$clubId/club/members" params={{ clubId: club.slug }} className="btn sm see-all">See all {members.length} members</Link>
      )}
      {canManage && <BlockedList clubId={club.id} />}
    </section>
  );
}
export const PREVIEW = 12;
